import type { TopoNodeKind } from "../../../support/dataImport/topology/topologyTypes";

// Network-topology glyphs, as pure path data.
//
// Drawn in the standard Cisco topology idiom: an arrowed puck is a router, an
// arrowed box is a switch, a brick wall is a firewall, a cylinder is storage, a
// cloud is somebody else's network. Original paths (not Cisco stencil artwork);
// they inherit `currentColor` and scale into the isometric projection.
//
// SELF-CONTAINED verbatim port from the example app's modules/topology/glyphs.ts.
// Every glyph is authored inside a 24×24 box centred on (12, 12).

export type GlyphPath = {
  d: string;
  /** Filled silhouette rather than an outline stroke. */
  fill?: boolean;
  opacity?: number;
};

/** Ellipse as a path, so a single primitive covers every renderer. */
function ellipse(cx: number, cy: number, rx: number, ry: number): string {
  return `M ${cx - rx} ${cy} a ${rx} ${ry} 0 1 0 ${2 * rx} 0 a ${rx} ${ry} 0 1 0 ${-2 * rx} 0 Z`;
}

function circle(cx: number, cy: number, r: number): string {
  return ellipse(cx, cy, r, r);
}

function rect(x: number, y: number, w: number, h: number): string {
  return `M ${x} ${y} h ${w} v ${h} h ${-w} Z`;
}

/** The four-arrow rosette that makes a router a router. */
const ROUTER_ARROWS: GlyphPath[] = [
  { d: "M11.4 6.6 L8.2 6.6 L9.4 5.4 L8.6 4.6 L6 7.2 L8.6 9.8 L9.4 9 L8.2 7.8 L11.4 7.8 Z", fill: true },
  { d: "M12.6 6.6 L15.8 6.6 L14.6 5.4 L15.4 4.6 L18 7.2 L15.4 9.8 L14.6 9 L15.8 7.8 L12.6 7.8 Z", fill: true, opacity: 0.55 },
  { d: "M11.4 17.4 L8.2 17.4 L9.4 18.6 L8.6 19.4 L6 16.8 L8.6 14.2 L9.4 15 L8.2 16.2 L11.4 16.2 Z", fill: true, opacity: 0.55 },
  { d: "M12.6 17.4 L15.8 17.4 L14.6 18.6 L15.4 19.4 L18 16.8 L15.4 14.2 L14.6 15 L15.8 16.2 L12.6 16.2 Z", fill: true },
];

/** Two opposed double-arrows — the switch marking. */
function switchArrows(y: number): GlyphPath[] {
  return [
    {
      d: `M6 ${y - 2.4} L15.4 ${y - 2.4} L15.4 ${y - 4} L18 ${y - 1.6} L15.4 ${y + 0.8} L15.4 ${y - 0.8} L6 ${y - 0.8} Z`,
      fill: true,
    },
    {
      d: `M18 ${y + 3.6} L8.6 ${y + 3.6} L8.6 ${y + 5.2} L6 ${y + 2.8} L8.6 ${y + 0.4} L8.6 ${y + 2} L18 ${y + 2} Z`,
      fill: true,
      opacity: 0.6,
    },
  ];
}

const CLOUD = "M6.6 17.5a3.6 3.6 0 0 1-.3-7.2 4.9 4.9 0 0 1 9.3-1.6 3.4 3.4 0 0 1 4.6 3.2 3.6 3.6 0 0 1-3.5 3.6H6.6Z";
const SWITCH_BODY: GlyphPath[] = [
  { d: "M3 8.5 L12 4.5 L21 8.5 L12 12.5 Z" },
  { d: "M3 8.5 v7 l9 4 l9 -4 v-7" },
];

export const TOPO_GLYPHS: Record<TopoNodeKind, GlyphPath[]> = {
  internet: [
    { d: circle(12, 12, 8) },
    { d: ellipse(12, 12, 3.4, 8) },
    { d: "M4.4 9.2h15.2" },
    { d: "M4.4 14.8h15.2" },
  ],

  cloud: [{ d: CLOUD }],

  cloud_app: [{ d: CLOUD }, { d: rect(9.4, 11.4, 5.2, 4) }, { d: "M11 13.4h2.2" }],

  sase: [
    { d: CLOUD },
    { d: "M10.2 15.2v-1.9a1.8 1.8 0 0 1 3.6 0v1.9" },
    { d: rect(9.4, 15, 5.2, 3.4), fill: true },
  ],

  cloud_mgmt: [
    { d: CLOUD },
    { d: circle(12, 13.2, 1.5) },
    { d: "M12 10.4v1.1M12 14.9v1.1M9.6 11.8l.9.6M13.5 14l.9.6M9.6 14.6l.9-.6M13.5 12.4l.9-.6" },
  ],

  router: [{ d: ellipse(12, 12, 9, 7.4) }, ...ROUTER_ARROWS],

  sdwan_edge: [
    { d: ellipse(12, 12, 9, 7.4) },
    { d: "M4.4 12c2.6-3.2 5.1-3.2 7.6 0s5 3.2 7.6 0" },
    { d: circle(12, 12, 1.6), fill: true },
    { d: "M12 3.4v2.2M12 18.4v2.2" },
  ],

  sp_router: [
    { d: ellipse(12, 12, 9, 7.4) },
    ...ROUTER_ARROWS,
    { d: circle(12, 12, 3.2), opacity: 0.7 },
  ],

  firewall: [
    { d: rect(3.5, 6, 17, 12) },
    { d: "M3.5 10h17M3.5 14h17M9 6v4M15 10v4M9 14v4" },
  ],

  identity: [
    { d: circle(10, 9, 3) },
    { d: "M4.5 19c0-3 2.5-5 5.5-5 1 0 1.9.2 2.7.6" },
    { d: circle(16.8, 14.6, 2.2) },
    { d: "M18.4 16.2 L21 18.8 l-1.2 1.2 l-1-1 l-1 1 l-1.2-1.2" },
  ],

  shield: [
    { d: "M12 3.5 L19 6 v6 c0 4.2-2.9 7-7 8.5 c-4.1-1.5-7-4.3-7-8.5 V6 Z" },
    { d: "M8.8 12.2 L11.1 14.5 L15.2 9.9" },
  ],

  l3_switch: [...SWITCH_BODY, ...switchArrows(13)],

  switch: [
    ...SWITCH_BODY,
    { d: "M6.5 12.6 L13 15.5 l-.1-1.5 l2.6.8 l-2.3 2.4 v-1.4 Z", fill: true },
  ],

  optics: [
    { d: rect(3.5, 9, 7, 6) },
    { d: "M10.5 12h3.2" },
    { d: "M14.5 7.4c3 1.6 3 7.6 0 9.2M17.2 5.6c4 2.4 4 10.4 0 12.8" },
  ],

  dc_switch: [
    ...SWITCH_BODY,
    { d: "M6.2 11.6 L12 14.2 l5.8-2.6 M12 14.2 v4.6", opacity: 0.8 },
    ...switchArrows(12.6),
  ],

  server: [
    { d: rect(6, 3.5, 12, 17) },
    { d: "M6 9h12M6 14.5h12" },
    { d: circle(9, 6.2, 0.9), fill: true },
    { d: circle(9, 11.7, 0.9), fill: true },
    { d: circle(9, 17.2, 0.9), fill: true },
  ],

  storage: [
    { d: ellipse(12, 6.5, 7, 2.8) },
    { d: "M5 6.5v11c0 1.6 3.1 2.8 7 2.8s7-1.2 7-2.8v-11" },
    { d: "M5 12c0 1.6 3.1 2.8 7 2.8s7-1.2 7-2.8", opacity: 0.7 },
  ],

  wlc: [
    { d: rect(3.5, 12, 17, 7) },
    { d: "M6.5 15.5h4M18 15.5h-2" },
    { d: "M12 12V8.5" },
    { d: "M8.6 7.4a4.8 4.8 0 0 1 6.8 0M10.4 9.6a2.3 2.3 0 0 1 3.2 0" },
  ],

  ap: [
    { d: ellipse(12, 16.5, 7, 3) },
    { d: "M5 16.5v1.6c0 1.6 3.1 2.8 7 2.8s7-1.2 7-2.8v-1.6" },
    { d: "M8.4 9.6a5 5 0 0 1 7.2 0M6 6.8a8.6 8.6 0 0 1 12 0M10.6 12.2a2.1 2.1 0 0 1 2.8 0" },
  ],

  iot_switch: [
    ...SWITCH_BODY,
    { d: circle(12, 13.2, 2.4) },
    { d: "M12 10.8V9.4M12 17v-1.4M9.6 13.2H8.2M15.8 13.2h-1.4" },
  ],

  iot_router: [
    { d: ellipse(12, 12, 9, 7.4) },
    { d: circle(12, 12, 2.6) },
    { d: "M12 9.4V7M12 17v-2.4M9.4 12H7M17 12h-2.4M9.9 9.9 8.2 8.2M15.8 15.8l-1.7-1.7M9.9 14.1l-1.7 1.7M15.8 8.2l-1.7 1.7" },
  ],

  phone: [
    { d: "M5.5 8.5h13l1.5 8.5a1.4 1.4 0 0 1-1.4 1.7H5.4A1.4 1.4 0 0 1 4 17Z" },
    { d: "M7.4 8.5V7a4.6 4.6 0 0 1 9.2 0v1.5" },
    { d: rect(9, 12.4, 6, 3.4) },
  ],

  video: [
    { d: rect(3, 5, 18, 11) },
    { d: "M8 20h8M12 16v4" },
    { d: circle(12, 10.5, 2.4), opacity: 0.8 },
  ],

  camera: [
    { d: "M3.5 9.5 L16 6 l1.2 4.4 L4.7 13.9 Z" },
    { d: "M17.2 8.2 L21 7 v6.6 l-3.5-1.2" },
    { d: "M6.5 13.4 L7.6 18 M10.6 12.3 L11.7 17" },
  ],

  iot: [
    { d: circle(12, 14.5, 3.4) },
    { d: circle(12, 14.5, 1.1), fill: true },
    { d: "M8.4 8.6a5.4 5.4 0 0 1 7.2 0M6 5.6a9.2 9.2 0 0 1 12 0" },
  ],

  analytics: [
    { d: rect(3.5, 4.5, 17, 15) },
    { d: "M7.5 15.5v-3.4M11 15.5V8.8M14.5 15.5v-5M18 15.5v-2" },
  ],

  monitor: [
    { d: circle(12, 12, 8.5) },
    { d: "M4.4 12h3.2l1.7-3.6 2.6 7.4 1.9-4.6 1.2 2.4h4.6" },
  ],

  ai_agent: [
    { d: "M12 3.2 L20 7.6 v8.8 L12 20.8 L4 16.4 V7.6 Z" },
    { d: circle(12, 9.4, 1.6), fill: true },
    { d: circle(8.6, 15, 1.6), fill: true },
    { d: circle(15.4, 15, 1.6), fill: true },
    { d: "M11 10.8 L9.4 13.6 M13 10.8 l1.6 2.8 M10.2 15 h3.6" },
  ],

  generic: [
    { d: rect(4, 6, 16, 12) },
    { d: "M4 10h16" },
    { d: circle(7, 8, 0.8), fill: true },
  ],
};

export function glyphPaths(kind: TopoNodeKind): GlyphPath[] {
  return TOPO_GLYPHS[kind] ?? TOPO_GLYPHS.generic;
}

export const GLYPH_STROKE_WIDTH = 1.5;
/** Authoring box. Renderers scale from this. */
export const GLYPH_VIEWBOX = 24;
